import { useEffect, useState } from "react";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import {
    FormGrid, FieldInput, FieldTextarea, FieldSelect, useForm,
} from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { ManualMarkdownEditor } from "../../_Manuals/_UI/ManualMarkdownEditor";

const toOptions = (rows) => rows.map(([value, label]) => ({ value, label }));

const MODULE_STATUS = toOptions([
    ["DRAFT", "Borrador"],
    ["IN_PROGRESS", "En preparacion"],
    ["READY", "Listo"],
    ["REVIEW", "Revisar"],
    ["COMPLETED", "Cerrado / visto"],
]);

const NOTE_TYPE = toOptions([
    ["TOPIC", "Tema a ensenar"],
    ["NOTE", "Apunte"],
    ["SUMMARY", "Resumen"],
    ["EXAMPLE", "Ejemplo"],
    ["EXERCISE", "Ejercicio"],
    ["QUESTION", "Pregunta / disparador"],
    ["RESOURCE", "Recurso"],
    ["IDEA", "Idea para clase"],
    ["CHECKLIST", "Checklist"],
    ["REFERENCE", "Referencia"],
    ["OTHER", "Otro"],
]);

const NOTE_STATUS = toOptions([
    ["TO_PREPARE", "Por preparar"],
    ["DRAFT", "Borrador"],
    ["READY", "Listo para usar"],
    ["TAUGHT", "Ya ensenado"],
    ["REVIEW", "Quiero revisarlo"],
]);

const TASK_TYPE = toOptions([
    ["PREPARE_CLASS", "Preparar clase"],
    ["REVIEW_TOPIC", "Revisar tema"],
    ["CREATE_EXAMPLE", "Crear ejemplo"],
    ["CREATE_EXERCISE", "Crear ejercicio"],
    ["RESEARCH", "Investigar"],
    ["MATERIAL", "Armar material"],
    ["FOLLOW_UP", "Seguimiento"],
    ["ADMIN", "Administrativo"],
    ["OTHER", "Otro"],
]);

const TASK_PRIORITY = toOptions([
    ["LOW", "Baja"],
    ["MEDIUM", "Media"],
    ["HIGH", "Alta"],
    ["URGENT", "Urgente"],
]);

const TASK_STATUS = toOptions([
    ["TODO", "Pendiente"],
    ["IN_PROGRESS", "En progreso"],
    ["BLOCKED", "Bloqueada"],
    ["DONE", "Hecha"],
    ["CANCELLED", "Cancelada"],
]);

const SCHEDULE_TYPE = toOptions([
    ["CLASS", "Clase"],
    ["STUDY", "Estudio / repaso"],
    ["PREPARATION", "Preparacion"],
    ["REVIEW", "Revision"],
    ["DEADLINE", "Fecha limite"],
    ["MEETING", "Reunion"],
    ["OTHER", "Otro"],
]);

const SCHEDULE_STATUS = toOptions([
    ["PLANNED", "Planificado"],
    ["CONFIRMED", "Confirmado"],
    ["DONE", "Realizado"],
    ["MOVED", "Reprogramado"],
    ["CANCELLED", "Cancelado"],
]);

const EMPTY = {
    module: {
        title: "",
        description: "",
        objectives: "",
        status: "DRAFT",
        estimatedMinutes: 0,
    },
    lesson: {
        title: "",
        moduleId: "",
        type: "TOPIC",
        description: "",
        content: "",
        instructorNotes: "",
        resourceUrl: "",
        status: "TO_PREPARE",
        durationMinutes: 0,
    },
    task: {
        title: "",
        moduleId: "",
        type: "PREPARE_CLASS",
        priority: "MEDIUM",
        status: "TODO",
        dueDate: "",
        estimatedMinutes: 0,
        description: "",
        notes: "",
    },
    schedule: {
        title: "",
        moduleId: "",
        type: "CLASS",
        scheduledAt: "",
        durationMinutes: 0,
        location: "",
        meetingUrl: "",
        status: "PLANNED",
        description: "",
        notes: "",
    },
};

const TITLE = {
    module: "modulo",
    lesson: "apunte / tema",
    task: "tarea",
    schedule: "evento",
};

// Contrato explicito React -> Nest. Nunca reenviar entidades completas de la API
// porque contienen id, courseId, timestamps, relaciones y otros campos de solo lectura.
const FIELDS_BY_KIND = {
    module: ["title", "description", "objectives", "status", "estimatedMinutes"],
    lesson: ["title", "moduleId", "type", "description", "content", "instructorNotes", "resourceUrl", "status", "durationMinutes"],
    task: ["title", "moduleId", "type", "priority", "status", "dueDate", "estimatedMinutes", "description", "notes"],
    schedule: ["title", "moduleId", "type", "scheduledAt", "durationMinutes", "location", "meetingUrl", "status", "description", "notes"],
};

const pickFields = (source, fields) => {
    const input = source && typeof source === "object" ? source : {};
    return fields.reduce((acc, key) => {
        if (Object.prototype.hasOwnProperty.call(input, key)) acc[key] = input[key];
        return acc;
    }, {});
};

const valuesOf = (options) => options.map((option) => option.value);

const integerBetween = (min, max, label = "El valor") => (value) => {
    if (value === "" || value === null || value === undefined) return null;
    const parsed = Number(value);
    if (!Number.isInteger(parsed)) return `${label} debe ser un numero entero`;
    if (parsed < min || parsed > max) return `${label} debe estar entre ${min} y ${max}`;
    return null;
};

const oneOf = (options, message) => (value) => (
    value === "" || value === null || value === undefined || valuesOf(options).includes(value)
        ? null
        : message
);

const dateTimeRule = (value) => {
    if (!value) return null;
    return Number.isNaN(new Date(value).getTime()) ? "Ingresa una fecha y hora validas" : null;
};

const SCHEMA_BY_KIND = {
    module: {
        title: { required: true, minLength: 2, maxLength: 180 },
        description: { maxLength: 5000 },
        objectives: { maxLength: 5000 },
        status: { custom: oneOf(MODULE_STATUS, "Elegi un estado valido para el modulo") },
        estimatedMinutes: { custom: integerBetween(0, 100000, "El tiempo estimado") },
    },
    lesson: {
        title: { required: true, minLength: 2, maxLength: 180 },
        moduleId: { required: true },
        type: { custom: oneOf(NOTE_TYPE, "Elegi un tipo de apunte valido") },
        status: { custom: oneOf(NOTE_STATUS, "Elegi un estado valido") },
        description: { maxLength: 10000 },
        content: { maxLength: 50000 },
        instructorNotes: { maxLength: 20000 },
        resourceUrl: { url: true, maxLength: 500 },
        durationMinutes: { custom: integerBetween(0, 100000, "El tiempo para explicarlo") },
    },
    task: {
        title: { required: true, minLength: 2, maxLength: 180 },
        type: { custom: oneOf(TASK_TYPE, "Elegi un tipo de tarea valido") },
        priority: { custom: oneOf(TASK_PRIORITY, "Elegi una prioridad valida") },
        status: { custom: oneOf(TASK_STATUS, "Elegi un estado valido") },
        dueDate: { date: true },
        estimatedMinutes: { custom: integerBetween(0, 100000, "El tiempo estimado") },
        description: { maxLength: 10000 },
        notes: { maxLength: 10000 },
    },
    schedule: {
        title: { required: true, minLength: 2, maxLength: 180 },
        type: { custom: oneOf(SCHEDULE_TYPE, "Elegi un tipo de evento valido") },
        status: { custom: oneOf(SCHEDULE_STATUS, "Elegi un estado valido") },
        scheduledAt: { custom: dateTimeRule },
        durationMinutes: { custom: integerBetween(0, 100000, "La duracion") },
        location: { maxLength: 180 },
        meetingUrl: { url: true, maxLength: 500 },
        description: { maxLength: 10000 },
        notes: { maxLength: 10000 },
    },
};

export const CourseItemModal = ({ kind, isOpen, onClose, item = null, onSave, modules = [] }) => {
    const currentKind = kind || "lesson";
    const [useMarkdownMode, setMarkdownMode] = useState("split");

    const FORM = useForm({
        initialValues: EMPTY[currentKind],
        schema: SCHEMA_BY_KIND[currentKind] || SCHEMA_BY_KIND.lesson,
        onSubmit: async (values) => {
            const allowedFields = FIELDS_BY_KIND[currentKind] || FIELDS_BY_KIND.lesson;
            const payload = {
                ...pickFields(values, allowedFields),
                title: String(values.title || "").trim(),
            };

            ["estimatedMinutes", "durationMinutes"].forEach((key) => {
                if (key in payload) payload[key] = Number(payload[key] || 0);
            });

            if ("moduleId" in payload) payload.moduleId = payload.moduleId || undefined;
            if ("dueDate" in payload) payload.dueDate = payload.dueDate || null;
            if ("scheduledAt" in payload) payload.scheduledAt = payload.scheduledAt
                ? new Date(payload.scheduledAt).toISOString()
                : null;
            if ("resourceUrl" in payload) payload.resourceUrl = String(payload.resourceUrl || "").trim() || undefined;
            if ("meetingUrl" in payload) payload.meetingUrl = String(payload.meetingUrl || "").trim() || undefined;
            if ("location" in payload) payload.location = String(payload.location || "").trim() || undefined;

            const result = await onSave?.(payload);

            if (result === false || result?.success === false) {
                if (result && typeof result === "object") FORM.applyApiError(result);
                return result;
            }

            onClose?.();
            return result;
        },
    });

    useEffect(() => {
        if (!isOpen) return;

        const base = EMPTY[currentKind] || EMPTY.lesson;
        const allowedFields = FIELDS_BY_KIND[currentKind] || FIELDS_BY_KIND.lesson;
        const safeItem = pickFields(item, allowedFields);
        const nextValues = { ...base, ...safeItem };

        if (currentKind === "task") {
            nextValues.dueDate = item?.dueDate || "";
        }

        if (currentKind === "schedule") {
            nextValues.scheduledAt = item?.scheduledAt
                ? String(item.scheduledAt).slice(0, 16)
                : "";
        }

        FORM.reset(nextValues);
    }, [isOpen, item, currentKind]); // eslint-disable-line react-hooks/exhaustive-deps

    const moduleOptions = currentKind === "lesson"
        ? modules.map((module) => ({ value: module.id, label: module.title }))
        : [
            { value: "", label: "Sin modulo" },
            ...modules.map((module) => ({ value: module.id, label: module.title })),
        ];

    const title = `${item?.id && !String(item.id).startsWith("new-") ? "Editar" : "Nuevo"} ${TITLE[currentKind]}`;

    return (
        <AppModal
            isOpen={isOpen}
            onClose={onClose}
            size={currentKind === "lesson" ? "full" : "lg"}
            isDirty={FORM.isDirty}
            title={title}
            description={currentKind === "lesson"
                ? "Escribi libremente lo que queres explicar, recordar, ejemplificar o revisar."
                : null}
            footer={
                <>
                    <AppButton variant="ghost" tone="neutral" onClick={onClose}>Cancelar</AppButton>
                    <AppButton onClick={FORM.onSubmit} isLoading={FORM.isSubmitting}>Guardar</AppButton>
                </>
            }
        >
            {FORM.serverError && (
                <div className="LStudio__academic_form_error_summary" role="alert" aria-live="assertive">
                    <strong>No se pudo guardar.</strong>
                    <span>{FORM.serverError}</span>
                    {FORM.requestId && <small>Referencia tecnica: {FORM.requestId}</small>}
                </div>
            )}

            {currentKind === "module" && (
                <FormGrid columns={3}>
                    <FieldInput {...FORM.field("title")} label="Nombre del modulo" placeholder="Ej: Active Record" span={2} />
                    <FieldSelect {...FORM.field("status")} label="Estado" options={MODULE_STATUS} />
                    <FieldTextarea {...FORM.field("description")} label="Descripcion" rows={4} span={3}
                        placeholder="Que temas agrupa este modulo o como queres encararlo." />
                    <FieldTextarea {...FORM.field("objectives")} label="Que quiero cubrir" rows={4} span={3}
                        placeholder="Objetivos, conceptos centrales, puntos que no quiero olvidarme." />
                    <FieldInput {...FORM.field("estimatedMinutes")} type="number" min="0"
                        label="Tiempo estimado (min)" />
                </FormGrid>
            )}

            {currentKind === "lesson" && (
                <div className="LStudio__academic_note_editor">
                    <FormGrid columns={3}>
                        <FieldInput {...FORM.field("title")} label="Titulo" placeholder="Ej: Diferencia entre includes y joins" span={2} />
                        <FieldSelect {...FORM.field("moduleId")} label="Modulo" options={moduleOptions} />
                        <FieldSelect {...FORM.field("type")} label="Tipo de apunte" options={NOTE_TYPE} />
                        <FieldSelect {...FORM.field("status")} label="Estado" options={NOTE_STATUS} />
                        <FieldInput {...FORM.field("durationMinutes")} type="number" min="0"
                            label="Tiempo para explicarlo (min)" />
                        <FieldTextarea {...FORM.field("description")} label="Resumen rapido" rows={4} span={3}
                            placeholder="Una idea corta para reconocer de que trata este apunte." />
                        <div className="LStudio__academic_markdown_field" data-field="content">
                            <div className="LStudio__academic_markdown_label">
                                <strong>Desarrollo / contenido para enseñar</strong>
                                <span>Markdown completo con títulos, listas, imágenes, tablas, enlaces, checklist y bloques de código.</span>
                            </div>
                            <ManualMarkdownEditor
                                value={FORM.values.content || ""}
                                onChange={(next) => FORM.setValue("content", next)}
                                mode={useMarkdownMode}
                                onModeChange={setMarkdownMode}
                                minHeight={480}
                                placeholder="## Concepto\n\nExplicación...\n\n- Punto importante\n- Otro punto\n\n```js\nconsole.log('ejemplo')\n```"
                                ariaLabel="Contenido Markdown del apunte"
                            />
                        </div>
                        <FieldTextarea {...FORM.field("instructorNotes")} label="Recordatorios privados" rows={7} resize="vertical" span={3}
                            placeholder="Cosas que solo vos necesitas recordar: errores comunes, aclaraciones, pendientes, ideas para mejorar..." />
                        <FieldInput {...FORM.field("resourceUrl")} type="url" label="Referencia o recurso" placeholder="https://..." span={2} />
                    </FormGrid>
                </div>
            )}

            {currentKind === "task" && (
                <FormGrid columns={3}>
                    <FieldInput {...FORM.field("title")} label="Tarea" placeholder="Ej: Preparar ejemplo de callbacks" span={2} />
                    <FieldSelect {...FORM.field("moduleId")} label="Modulo relacionado" options={moduleOptions} />
                    <FieldSelect {...FORM.field("type")} label="Tipo" options={TASK_TYPE} />
                    <FieldSelect {...FORM.field("priority")} label="Prioridad" options={TASK_PRIORITY} />
                    <FieldSelect {...FORM.field("status")} label="Estado" options={TASK_STATUS} />
                    <FieldInput {...FORM.field("dueDate")} type="date" label="Fecha objetivo" />
                    <FieldInput {...FORM.field("estimatedMinutes")} type="number" min="0" label="Tiempo estimado (min)" />
                    <FieldTextarea {...FORM.field("description")} label="Descripcion" rows={4} span={3} />
                    <FieldTextarea {...FORM.field("notes")} label="Notas" rows={4} span={3} />
                </FormGrid>
            )}

            {currentKind === "schedule" && (
                <FormGrid columns={3}>
                    <FieldInput {...FORM.field("title")} label="Titulo" placeholder="Ej: Repasar autenticacion antes de la clase" span={2} />
                    <FieldSelect {...FORM.field("moduleId")} label="Modulo relacionado" options={moduleOptions} />
                    <FieldSelect {...FORM.field("type")} label="Tipo" options={SCHEDULE_TYPE} />
                    <FieldSelect {...FORM.field("status")} label="Estado" options={SCHEDULE_STATUS} />
                    <FieldInput {...FORM.field("scheduledAt")} type="datetime-local" label="Fecha y hora" />
                    <FieldInput {...FORM.field("durationMinutes")} type="number" min="0" label="Duracion (min)" />
                    <FieldInput {...FORM.field("location")} label="Lugar / referencia" placeholder="Ej: Aula, Meet, oficina..." />
                    <FieldInput {...FORM.field("meetingUrl")} type="url" label="Enlace" placeholder="https://..." span={2} />
                    <FieldTextarea {...FORM.field("description")} label="Descripcion" rows={4} span={3} />
                    <FieldTextarea {...FORM.field("notes")} label="Notas privadas" rows={4} span={3} />
                </FormGrid>
            )}
        </AppModal>
    );
};
